import { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import type { ProductListFacets, ProductListItem, ProductListQuery } from "@/lib/api-client";
import { ActiveFilters } from "./ActiveFilters";
import { FilterPanel } from "./FilterPanel";
import { ProductGrid } from "./ProductGrid";
import { SortBar } from "./SortBar";

export interface ListingControlsProps {
  facets: ProductListFacets;
  total: number;
  initialItems: ProductListItem[];
  initialNextCursor: string | null;
  query: ProductListQuery;
}

/**
 * Porté depuis apps/web/components/product/ListingControls.tsx. Orchestre le
 * bloc 3 (barre d'outils), le bloc 4 (filtres) et le bloc 5 (grille) de
 * VVS_MODELE_INTERFACE.md §3.1. Seul changement : `useSearchParams()` de
 * react-router-dom au lieu de next/navigation.
 *
 * `key={JSON.stringify(query)}` sur `ProductGrid` : cf. commentaire de tête
 * de ce composant pour la justification (remontage forcé à chaque changement
 * de filtres/tri, pour remplacer le rendu serveur par navigation d'apps/web).
 */
export function ListingControls({ facets, total, initialItems, initialNextCursor, query }: ListingControlsProps) {
  const [isFilterOpen, setFilterOpen] = useState(false);
  const [searchParams] = useSearchParams();

  const activeFilterCount = useMemo(() => {
    let count = 0;
    const brand = searchParams.get("brand");
    if (brand) count += brand.split(",").filter(Boolean).length;
    count += searchParams.getAll("attribute").length;
    if (searchParams.get("priceMin") || searchParams.get("priceMax")) count += 1;
    if (searchParams.get("rating")) count += 1;
    if (searchParams.get("inStock") === "true") count += 1;
    return count;
  }, [searchParams]);

  return (
    <>
      <SortBar total={total} activeFilterCount={activeFilterCount} onOpenFilters={() => setFilterOpen(true)} />
      <ActiveFilters facets={facets} />

      <div className="lg:grid lg:grid-cols-12 lg:gap-8 lg:py-6">
        <FilterPanel facets={facets} isOpen={isFilterOpen} onClose={() => setFilterOpen(false)} />
        <div className="py-4 lg:col-span-9 lg:py-0">
          <ProductGrid
            key={JSON.stringify(query)}
            initialItems={initialItems}
            initialNextCursor={initialNextCursor}
            query={query}
          />
        </div>
      </div>
    </>
  );
}
